import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';

import { useAuth } from '../auth/AuthContext';
import { Logo } from '../components/Logo';
import { Alert } from '../components/ui/Alert';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { errorMessage } from '../lib/format';

// Mirrors the api's OTP_RESEND_COOLDOWN_SECONDS default (services/api/src/
// config/env.ts). The backend enforces its own cooldown server-side and
// never exposes the remaining time to the client, so this is a local
// approximation, not a synced value — if that env var is ever tuned in
// production, this constant should be updated to match.
const RESEND_COOLDOWN_SECONDS = 60;

interface LocationState {
  from?: { pathname: string };
}

type Step = 'identifier' | 'code';

export function LoginPage(): JSX.Element {
  const { status, requestOtp, verifyOtp } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [step, setStep] = useState<Step>('identifier');
  const [identifier, setIdentifier] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  // Only ever set when the server has OTP_DEV_EXPOSE_CODE enabled (never in
  // production) — a local-testing convenience for when the email/WhatsApp
  // provider can't actually deliver.
  const [devCode, setDevCode] = useState<string | null>(null);

  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = window.setInterval(() => setCooldown((s) => Math.max(0, s - 1)), 1000);
    return () => window.clearInterval(timer);
  }, [cooldown]);

  // Already signed in → skip the form.
  if (status === 'authenticated') {
    const dest = (location.state as LocationState | null)?.from?.pathname ?? '/app';
    return <Navigate to={dest} replace />;
  }

  const onSubmitIdentifier = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const dev = await requestOtp(identifier.trim());
      setDevCode(dev ?? null);
      setCode(dev ?? '');
      setStep('code');
      setCooldown(RESEND_COOLDOWN_SECONDS);
    } catch (err) {
      setError(errorMessage(err, 'Unable to send a code right now. Please try again.'));
    } finally {
      setSubmitting(false);
    }
  };

  const onResend = async (): Promise<void> => {
    if (cooldown > 0) return;
    setError(null);
    try {
      const dev = await requestOtp(identifier.trim());
      setDevCode(dev ?? null);
      setCode(dev ?? '');
      setCooldown(RESEND_COOLDOWN_SECONDS);
    } catch (err) {
      setError(errorMessage(err, 'Unable to send a code right now. Please try again.'));
    }
  };

  const onSubmitCode = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await verifyOtp(identifier.trim(), code.trim());
      const dest = (location.state as LocationState | null)?.from?.pathname ?? '/app';
      navigate(dest, { replace: true });
    } catch (err) {
      setError(errorMessage(err, "That code didn't work — check it and try again."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-background">
      {/* Brand panel — fills its half at every width from md up, no bare
       * "floating form" state between breakpoints. Below md, a compact
       * version collapses to a top banner instead of disappearing outright. */}
      <div className="relative overflow-hidden bg-brand-950 md:min-h-screen min-h-[14rem]">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: "url('/images/login-hero.webp')" }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-brand-950/95 via-brand-950/50 to-brand-950/20" />
        <div className="relative h-full flex flex-col justify-between p-8 sm:p-10 md:p-12 text-white min-h-[14rem] md:min-h-screen">
          <Logo compact />
          <div className="max-w-md">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-accent">
              Sanath Gamage Coaching
            </p>
            <p className="mt-3 text-2xl sm:text-3xl font-display font-medium leading-snug">
              Your personalised coaching journey, one step at a time.
            </p>
          </div>
        </div>
      </div>

      <div className="flex flex-col items-center justify-center gap-8 px-6 py-12 sm:px-10 md:px-16">
        <div className="w-full max-w-sm">
          <AnimatePresence mode="wait">
            {step === 'identifier' && (
              <motion.form
                key="identifier-step"
                initial={{ opacity: 0, x: -12 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -12 }}
                onSubmit={(e) => void onSubmitIdentifier(e)}
                className="flex flex-col gap-5"
              >
                <div className="text-center">
                  <h1 className="text-xl font-display font-medium text-foreground">Welcome back</h1>
                  <p className="text-sm text-muted-foreground mt-1">
                    Enter your email or phone number and we&rsquo;ll send you a login code.
                  </p>
                </div>
                {error && <Alert tone="danger">{error}</Alert>}
                <Input
                  label="Email or phone"
                  placeholder="you@example.com"
                  value={identifier}
                  onChange={(e) => setIdentifier(e.target.value)}
                  autoFocus
                  autoComplete="username"
                />
                <Button
                  type="submit"
                  size="lg"
                  block
                  loading={submitting}
                  disabled={identifier.trim() === ''}
                >
                  Send code
                </Button>
              </motion.form>
            )}

            {step === 'code' && (
              <motion.form
                key="code-step"
                initial={{ opacity: 0, x: 12 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 12 }}
                onSubmit={(e) => void onSubmitCode(e)}
                className="flex flex-col gap-5"
              >
                <div className="text-center">
                  <h1 className="text-xl font-display font-medium text-foreground">
                    Enter your code
                  </h1>
                  <p className="text-sm text-muted-foreground mt-1">
                    If an account exists for{' '}
                    <span className="font-medium text-foreground">{identifier}</span>, we&rsquo;ve
                    sent a 6-digit code. It expires in 5 minutes.
                  </p>
                </div>
                {error && <Alert tone="danger">{error}</Alert>}
                {devCode && (
                  <Alert tone="info">
                    Dev mode: code auto-filled below (<strong>{devCode}</strong>). This only appears
                    when the server has <code>OTP_DEV_EXPOSE_CODE</code> enabled.
                  </Alert>
                )}
                <Input
                  label="Login code"
                  placeholder="123456"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  autoFocus
                />
                <Button
                  type="submit"
                  size="lg"
                  block
                  loading={submitting}
                  disabled={code.trim().length !== 6}
                >
                  Sign in
                </Button>
                <div className="flex items-center justify-between text-sm">
                  <button
                    type="button"
                    onClick={() => {
                      setStep('identifier');
                      setCode('');
                      setError(null);
                    }}
                    className="text-muted-foreground hover:text-foreground"
                  >
                    &larr; Use a different email/phone
                  </button>
                  <button
                    type="button"
                    onClick={() => void onResend()}
                    disabled={cooldown > 0}
                    className="text-primary hover:text-primary/80 disabled:text-muted-foreground/60 disabled:cursor-not-allowed font-medium"
                  >
                    {cooldown > 0 ? `Resend in ${cooldown}s` : 'Resend code'}
                  </button>
                </div>
              </motion.form>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}
